import React, { useCallback, useState } from 'react'
import { Box, Dialog, DialogContent } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'

import SwipeableDrawerWithBackNavigation from '@/src/components/common/ui/swipeableDrawer'
import OfferDetailClient from '@/src/components/pages/offerDetail/OfferDetailClient'
import useCouponTray from '@/src/hooks/itinerary/useCouponTray'
import { DeviceType } from '@/src/types/deviceType'
import { getOfferSlugFromLink } from '@/src/utils/offerDetailUtils'

import confettiAnimation from '@/src/assets/animations/confetti.json'

import CashbackScheduleModalWrapper from './cashbackScheduleModal'
import CouponTray from './couponTray'
import CouponSuccessModalWrapper from './successModal'

const INLINE_COUPON_LIMIT = 2
/** Desktop inline tray: ~3 coupon rows visible with internal scroll */
const INLINE_DESKTOP_OFFERS_LIST_MAX_HEIGHT = 3 * 94.5 + 2 * 10

interface CouponTrayWrapperProps {
    deviceType: DeviceType
    onSuccessfulApply?: (message: string) => void
}

const CouponTrayWrapper = ({ deviceType, onSuccessfulApply }: CouponTrayWrapperProps) => {
    // ✅ ALL business logic here - following CODEBASE_CONTEXT guidelines
    const isMobile = deviceType === 'MOBILE'

    // Handle successful apply (coupon or gift voucher); desktop shows success modal, mobile passes to parent
    const handleSuccessfulApply = useCallback(
        (message: string) => {
            if (isMobile) {
                // For mobile, just pass the message to parent (mobile drawer handles modal)
                onSuccessfulApply?.(message)
            } else {
                // For desktop, show success modal
                setSuccessMessage(message)
                setSuccessModalOpen(true)
            }
        },
        [isMobile, onSuccessfulApply]
    )

    const couponTray = useCouponTray(handleSuccessfulApply)
    const tCommon = useTranslations('common')
    const locale = useLocale() || 'en'

    const [cashbackModalOpen, setCashbackModalOpen] = useState(false)
    const [knowMoreDrawerOpen, setKnowMoreDrawerOpen] = useState(false)
    const [knowMoreSlug, setKnowMoreSlug] = useState<string>('')
    const [successModalOpen, setSuccessModalOpen] = useState(false)
    const [successMessage, setSuccessMessage] = useState('')
    const [viewAllModalOpen, setViewAllModalOpen] = useState(false)

    const allOffers = couponTray.offers ?? []
    const displayedOffers = isMobile ? allOffers : allOffers.slice(0, INLINE_COUPON_LIMIT)
    const showViewAllCta = !isMobile && allOffers.length > INLINE_COUPON_LIMIT

    const appendLngParam = useCallback(
        (url: string) => {
            const lng = locale
            const base = typeof window !== 'undefined' ? window.location.origin : undefined
            const u = new URL(url, base)
            u.searchParams.set('lng', lng)
            return u.toString()
        },
        [locale]
    )

    const handleOpenCashbackModal = useCallback(() => {
        setCashbackModalOpen(true)
    }, [])

    const handleCloseCashbackModal = useCallback(() => {
        setCashbackModalOpen(false)
    }, [])
    const handleKnowMore = useCallback(
        (offerId: string) => {
            const offer = (couponTray.offers || []).find(o => o.id === offerId)
            const link = offer?.knowMoreLink
            if (!link) return

            if (isMobile) {
                const slug = getOfferSlugFromLink(link)
                if (slug) {
                    setKnowMoreSlug(slug)
                    setKnowMoreDrawerOpen(true)
                }
            } else {
                const urlWithLng = appendLngParam(link)
                if (typeof window !== 'undefined') {
                    window.open(urlWithLng, '_blank', 'noopener,noreferrer')
                }
            }
        },
        [couponTray.offers, isMobile, appendLngParam]
    )
    const closeKnowMoreDrawer = useCallback(() => setKnowMoreDrawerOpen(false), [])

    const handleSuccessModalClose = useCallback(() => {
        setSuccessModalOpen(false)
        setSuccessMessage('')
    }, [])

    const handleOpenViewAllModal = useCallback(() => setViewAllModalOpen(true), [])
    const handleCloseViewAllModal = useCallback(() => setViewAllModalOpen(false), [])
    const handleApplyFromModal = useCallback(
        (payload: { code: string; source: string; index: number }) => {
            couponTray.onApply(payload)
            handleCloseViewAllModal()
        },
        [couponTray, handleCloseViewAllModal]
    )

    return (
        couponTray.showCoupon && (
            <>
                <CouponTray
                    {...couponTray}
                    offers={displayedOffers}
                    offerIndexSource={allOffers}
                    offersListMaxHeight={isMobile ? undefined : INLINE_DESKTOP_OFFERS_LIST_MAX_HEIGHT}
                    locale={locale}
                    isMobile={isMobile}
                    tCommon={tCommon}
                    onOpenCashbackModal={handleOpenCashbackModal}
                    onKnowMore={handleKnowMore}
                    showViewAllCta={showViewAllCta}
                    onViewAll={isMobile ? undefined : handleOpenViewAllModal}
                />
                {!isMobile && (
                    <Dialog
                        open={viewAllModalOpen}
                        onClose={handleCloseViewAllModal}
                        maxWidth={false}
                        fullWidth={false}
                        slotProps={{
                            paper: {
                                sx: {
                                    borderRadius: 2,
                                    maxHeight: '90vh',
                                    width: 'min(calc(100vw - 48px), 508px)',
                                    p: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    overflow: 'hidden'
                                }
                            }
                        }}
                    >
                        <DialogContent
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                p: 0,
                                overflow: 'auto'
                            }}
                        >
                            <CouponTray
                                {...couponTray}
                                offers={allOffers}
                                locale={locale}
                                isMobile={false}
                                tCommon={tCommon}
                                onOpenCashbackModal={handleOpenCashbackModal}
                                onKnowMore={handleKnowMore}
                                onApply={handleApplyFromModal}
                                insideModal
                            />
                        </DialogContent>
                    </Dialog>
                )}
                {isMobile && couponTray.cashbackSchedule && (
                    <CashbackScheduleModalWrapper
                        open={cashbackModalOpen}
                        onClose={handleCloseCashbackModal}
                        cashbackSchedule={couponTray.cashbackSchedule}
                    />
                )}
                {isMobile && (
                    <SwipeableDrawerWithBackNavigation
                        open={knowMoreDrawerOpen}
                        onClose={closeKnowMoreDrawer}
                        sx={{ zIndex: 1400 }}
                    >
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                            {knowMoreSlug && (
                                <OfferDetailClient offerSlug={knowMoreSlug} />
                            )}
                        </Box>
                    </SwipeableDrawerWithBackNavigation>
                )}

                {/* Success Modal - Shows only for desktop */}
                {!isMobile && (
                    <CouponSuccessModalWrapper
                        open={successModalOpen}
                        successMessage={successMessage}
                        onClose={handleSuccessModalClose}
                        animationData={confettiAnimation}
                        autoCloseDelay={2500}
                    />
                )}
            </>
        )
    )
}

export default CouponTrayWrapper
